import React from 'react';
import './Projects.css';

const projects = [
  {
    number: 'I',
    label: 'Internship · 2026',
    title: 'HarvestLink: Farm Traceability System',
    client: 'Langer Farms',
    description:
      'Led a four-person engineering team contracted by Langer Farms to replace manual paper and Excel tracking with a production-grade digital traceability pipeline. Delivered a system spanning field-level data capture to full audit trail, competing for client selection at the May 2026 Innovate To Grow Event.',
    highlights: [
      'Architected the FastAPI REST backend and PostgreSQL schema tracking Block/Line → Bin → Tractor → Truck → Packing House lineage using SQLAlchemy with immutable data integrity',
      'Engineered an automated Excel export service using openpyxl that dynamically formatted raw PostgreSQL data to exactly match the client\'s legacy paper reporting structures, supporting bilingual English and Spanish operation',
      'Authored 30 unit and integration tests using pytest, achieved 65-68% coverage using isolated SQLite dependency overrides, and remediated a critical GitGuardian security vulnerability',
    ],
    tech: ['Python', 'FastAPI', 'PostgreSQL', 'SQLAlchemy', 'pytest', 'openpyxl', 'React Native', 'Docker'],
    role: 'Team Lead · Backend Developer',
    github: null,
    tool: null,
  },
  {
    number: 'II',
    label: 'Berkeley · Summer 2026',
    title: 'CS161: Secure File-Sharing System',
    client: 'Cryptographic File-Sharing Platform',
    description:
      'A five-phase secure file-sharing system in Go: Argon2 authentication, encrypted file storage and append, public-key sharing, and access revocation. Designed for efficient appends and validated against a full security audit.',
    highlights: [
      'Architected constant-size metadata pointing to separate structures (chunk list, sharing graph, pending invitations) so appends cost O(append size) instead of re-encrypting the whole file',
      'Resolved multi-device consistency and UUID-serialization bugs via session-key datastore refresh and boundary-level type conversion',
      'Passed 30/30 local tests, 24/24 autograder tests, and a self-run 25-vector security audit across six attack phases with zero vulnerabilities found',
    ],
    tech: ['Go', 'Cryptography', 'Testing', 'Security Audit'],
    role: 'Team Implementation · Partner: Vladyslav Rutskyi',
    github: null,
    tool: null,
  },
  {
    number: 'III',
    label: 'Web Tool · 2025 – Present',
    title: 'ARTyping Template Generator',
    client: 'Image Processing Tool',
    description:
      'Built a real-time image-to-ASCII conversion engine using the Canvas API. Features HiDPI rendering at 2–3x pixel ratio, physical DPI-accurate paper modeling, and a rotation-independent character grid that preserves custom edits through encoding changes. Includes run-length encoding, configurable space counting, per-cell keyboard editing, session persistence, and a comprehensive input validation and security hardening layer.',
    highlights: [
      'HiDPI Canvas API rendering pipeline with proportional cell sampling to eliminate edge clipping across all image scales and rotation angles',
      'Run-length encoding and space counting guide system with configurable bracket symbols, colored overlays, and full-width margin-to-margin row encoding',
      'Input validation suite with file type allowlist, 20MB size cap, canvas overflow guard, and localStorage session sanitization — backed by 93 automated tests',
    ],
    tech: ['React', 'Canvas API', 'JavaScript', 'Jest'],
    role: 'Solo Developer',
    github: 'https://github.com/dtech982',
    tool: '?artyping',
  },
  {
    number: 'IV',
    label: 'Portfolio · 2025 – Present',
    title: 'Personal Portfolio',
    client: 'dmitriyfilimonov.com',
    description:
      'Designed and deployed a full-stack portfolio site featuring custom URL-param routing, an interactive business card with contact hotspots, a QR code generator with color presets and transparent PNG export, and a contact form with Web3Forms API integration. Hardened with Content Security Policy headers, input sanitization, and error handling throughout.',
    highlights: [
      'Custom React routing via URL query params with dedicated pages for business card, QR generator, and ARTyping tool — no router library required',
      'Interactive business card with five tap-target hotspots, swipe-to-flip animation, and Save Contact Details (vCard) download',
      'Production security hardening: CSP headers via vercel.json, file upload validation, canvas size guards, and sanitized localStorage session management',
    ],
    tech: ['React', 'JavaScript', 'CSS', 'Vercel'],
    role: 'Solo Developer',
    github: 'https://github.com/dtech982/dmitriyfilimonov',
    tool: null,
  },
];

export default function Projects({ onWebAppsOpen }) {
  return (
    <section className="projects" id="projects">
      <div className="projects__inner">

        <div className="projects__header-wrap">
          <div className="section-header">
            <p className="section-label">Selected Work</p>
            <h2 className="section-title">Projects</h2>
            <div className="section-rule">
              <span className="section-rule__line" />
              <span className="section-rule__diamond" />
              <span className="section-rule__line" />
            </div>
          </div>

          {/* Tacked-up flyer -- opens the Web Apps playbill panel */}
          <button
            type="button"
            className="projects-flyer"
            onClick={onWebAppsOpen}
            aria-label="Open Web Apps: Hey there Pal! Don't pass it by, give these nifty apps a try!"
          >
            <span className="projects-flyer__tack" aria-hidden="true" />
            <span className="projects-flyer__hey">Hey there Pal!</span>
            <span className="projects-flyer__figure" aria-hidden="true">E</span>
            <span className="projects-flyer__pitch">Don't pass it by, give these nifty apps a try!</span>
          </button>
        </div>

        <div className="projects__list">
          {projects.map((project) => (
            <article className="project-card" key={project.number}>
              <div className="project-card__numeral">{project.number}</div>

              <div className="project-card__body">
                <div className="project-card__header">
                  <div>
                    <p className="section-label project-card__label">{project.label}</p>
                    <h3 className="project-card__title">{project.title}</h3>
                    <p className="project-card__client">{project.client}</p>
                  </div>
                  <div className="project-card__role">
                    <p className="section-label">Role</p>
                    <p className="project-card__role-value">{project.role}</p>
                  </div>
                </div>

                <p className="project-card__description">{project.description}</p>

                <ul className="project-card__highlights">
                  {project.highlights.map((h, i) => (
                    <li key={i}>
                      <span className="project-card__bullet">◆</span>
                      {h}
                    </li>
                  ))}
                </ul>

                <div className="project-card__footer">
                  <div className="project-card__tech">
                    {project.tech.map((t) => (
                      <span className="project-card__tag" key={t}>{t}</span>
                    ))}
                  </div>
                  <div className="project-card__links">
                    {project.tool && (
                      <a
                        href={project.tool}
                        className="project-card__link project-card__link--tool"
                      >
                        Launch Tool <span className="kutz-icon" aria-hidden="true">D</span>
                      </a>
                    )}
                    {project.github ? (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-card__link"
                      >
                        View on GitHub →
                      </a>
                    ) : (
                      <span className="section-label">Private repository — available on request</span>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

      </div>
    </section>
  );
}